import { ReactNode, useState } from "react";
import { Alert, Button, IconButton, Stack } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router-dom";
import { useGetUserDetails } from "src/helpers/UserDetails";

/** Where the Library pane lives: the AtomAssist workspace. */
export const LIBRARY_PANE_PATH = "/assistant/projects";

const dismissalKey = (noticeId: string, userId: number | string) =>
  `library-move-notice-dismissed:${noticeId}:${userId}`;

// Reading `localStorage` throws outright where storage is blocked (Safari
// private browsing, a hardened enterprise profile), and this banner renders on
// the datasets page and inside the global search modal — an unguarded read
// during render would take those surfaces down rather than lose a dismissal.
// Same posture as `recordActions/revisionNotice.ts`: degrade to "not
// dismissed", which shows a notice the user may have already dismissed instead
// of showing nothing at all.
const readDismissed = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const writeDismissed = (key: string): void => {
  try {
    localStorage.setItem(key, "true");
  } catch {
    // Storage unavailable or full: the dismissal holds for this page view
    // only, which is better than failing the click.
  }
};

interface MovedToLibraryNoticeProps {
  /** Distinguishes each mount's dismissal from the others'. */
  noticeId: string;
  children: ReactNode;
  linkLabel?: string;
  /** Compact styling for tight hosts (e.g. inside a modal). */
  dense?: boolean;
  /** Runs after the link navigates (e.g. to close a hosting modal). */
  onNavigate?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * Dismissible-per-user banner telling an old surface's users where its
 * capability is moving (the Library pane), with a one-click way there.
 * States a direction, never a date (functional spec §22.3).
 */
export default function MovedToLibraryNotice({
  noticeId,
  children,
  linkLabel = "Open the Library",
  dense = false,
  onNavigate,
  sx,
}: MovedToLibraryNoticeProps) {
  const history = useHistory();
  const user = useGetUserDetails();
  // Per-user, per-notice: dismissal lives in localStorage keyed by user id,
  // so one user's dismissal never hides the notice from another on a shared
  // machine, and each surface's notice dismisses independently.
  const storageKey = dismissalKey(noticeId, user?.id ?? "anonymous");
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    writeDismissed(storageKey);
    setDismissed(true);
  };

  const handleNavigate = () => {
    history.push(LIBRARY_PANE_PATH);
    onNavigate?.();
  };

  return (
    <Alert
      severity="info"
      icon={dense ? false : undefined}
      data-testid={`moved-to-library-notice-${noticeId}`}
      action={
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Button
            color="inherit"
            size="small"
            onClick={handleNavigate}
            data-testid={`moved-to-library-notice-${noticeId}-open`}
            data-attr={`moved-to-library-notice:btn:open-library-${noticeId}`}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {linkLabel}
          </Button>
          <IconButton
            aria-label="Dismiss notice"
            color="inherit"
            size="small"
            onClick={handleDismiss}
            data-testid={`moved-to-library-notice-${noticeId}-dismiss`}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      }
      sx={{
        alignItems: "center",
        ...(dense && { py: 0, "& .MuiAlert-message": { py: 0.75 } }),
        ...sx,
      }}
    >
      {children}
    </Alert>
  );
}
